import React from 'react'
import Svg, { Path } from 'react-native-svg'
import { twMerge } from 'tailwind-merge'

import { toneClasses, type TypographyTone } from '../typography/tones'
import type { FigmaGlyph } from './glyph'

export type IconProps = {
    glyph: FigmaGlyph
    tone?: TypographyTone
    className?: string
    testID?: string
}

/**
 * The glyph carries its own box, because Figma draws these at more than one —
 * 24px navigation icons, 52px pictograms — and choosing one for the other is a
 * design decision this package does not get to make. A box rather than a side:
 * an icon is square and the icon dumper refuses one that is not, but a glyph
 * inside a component is under no such rule, and `Checkbox` draws its tick in a
 * 14x10 one.
 *
 * `currentColor` is what makes `tone` work: the wrapper carries the ink class
 * and every path inherits it, so an icon takes colour from the same ten Figma
 * `content/*` tokens as text.
 */
export const Icon = ({ glyph, tone = 'default', className, testID }: IconProps) => (
    <Svg
        width={glyph.width}
        height={glyph.height}
        viewBox={`0 0 ${glyph.width} ${glyph.height}`}
        className={twMerge(toneClasses[tone], className)}
        testID={testID ?? glyph.name}
    >
        {glyph.paths.map((path, index) => (
            <Path
                key={index}
                d={path.d}
                fill={path.stroke ? 'none' : 'currentColor'}
                fillRule={path.fillRule}
                clipRule={path.clipRule}
                stroke={path.stroke ? 'currentColor' : undefined}
                strokeWidth={path.stroke?.width}
                strokeLinecap={path.stroke?.linecap}
                strokeLinejoin={path.stroke?.linejoin}
            />
        ))}
    </Svg>
)

Icon.displayName = 'Icon'
